import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { UNIT_TYPES, ROOM_TYPES, ASPECTS, VIEWS, UNIT_STATUSES, formatPrice } from "@/lib/constants";

export default function BulkPriceUpdate() {
  const [projects, setProjects] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [form, setForm] = useState({
    project_id: "", block_id: "", unit_type: "", room_type: "",
    aspect: "", view: "", currency: "",
    statuses: ["Satışta", "Güncellemede"],
    update_type: "percent_inc", value: 0,
    force_locked: false,
  });
  const [result, setResult] = useState(null);
  const [previewRows, setPreviewRows] = useState(null);
  const [showLockedConfirm, setShowLockedConfirm] = useState(false);

  useEffect(() => { api.get("/projects").then(({ data }) => setProjects(data)); }, []);
  useEffect(() => {
    if (form.project_id) api.get(`/projects/${form.project_id}/blocks`).then(({ data }) => setBlocks(data));
    else setBlocks([]);
  }, [form.project_id]);

  const calcNewPrice = (oldPrice) => {
    const v = Number(form.value) || 0;
    if (form.update_type === "percent_inc") return Math.round(oldPrice * (1 + v / 100));
    if (form.update_type === "amount_add") return oldPrice + v;
    if (form.update_type === "amount_sub") return Math.max(0, oldPrice - v);
    if (form.update_type === "set") return v;
    return oldPrice;
  };

  const preview = async () => {
    const params = {};
    Object.entries({ project_id: form.project_id, block_id: form.block_id,
                     unit_type: form.unit_type, room_type: form.room_type,
                     aspect: form.aspect, view: form.view, currency: form.currency }).forEach(([k, v]) => { if (v) params[k] = v; });
    const { data } = await api.get("/units", { params });
    let filtered = data.filter((u) => form.statuses.includes(u.status));
    if (!form.force_locked) {
      filtered = filtered.filter((u) => !["Rezerve", "Kapora Alındı", "Satıldı", "Teslim Edildi"].includes(u.status));
    }
    setPreviewRows(filtered.map((u) => ({
      ...u, new_price: calcNewPrice(u.price || 0),
    })));
  };

  const doApply = async () => {
    try {
      const payload = { ...form, value: Number(form.value) };
      Object.keys(payload).forEach((k) => payload[k] === "" && delete payload[k]);
      const { data } = await api.post("/units/bulk-price-update", payload);
      setResult(data);
      toast.success(`Toplu fiyat güncelleme tamamlandı. ${data.updated} bağımsız bölüm güncellendi.`);
      preview();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const apply = () => {
    const hasLocked = form.statuses.some((s) => ["Rezerve", "Kapora Alındı", "Satıldı", "Teslim Edildi"].includes(s));
    if (hasLocked && form.force_locked) {
      setShowLockedConfirm(true);
      return;
    }
    if (!window.confirm("Toplu fiyat güncelleme uygulansın mı?")) return;
    doApply();
  };

  const confirmLocked = () => {
    setShowLockedConfirm(false);
    doApply();
  };

  const toggleStatus = (s) => {
    setForm((f) => ({ ...f, statuses: f.statuses.includes(s) ? f.statuses.filter((x) => x !== s) : [...f.statuses, s] }));
  };

  return (
    <div className="space-y-6" data-testid="bulk-price-page">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 font-display">Toplu Fiyat Güncelleme</h1>
        <p className="text-slate-500 mt-1">Filtreler ile uygun bağımsız bölümlerin fiyatını topluca güncelleyin.</p>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Sel l="Proje" v={form.project_id} on={(v) => setForm({ ...form, project_id: v, block_id: "" })}
               opts={[["", "Tümü"], ...projects.map((p) => [p.id, p.name])]} t="bulk-project" />
          <Sel l="Blok" v={form.block_id} on={(v) => setForm({ ...form, block_id: v })}
               opts={[["", "Tümü"], ...blocks.map((b) => [b.id, b.name])]} t="bulk-block" />
          <Sel l="Bölüm Tipi" v={form.unit_type} on={(v) => setForm({ ...form, unit_type: v })}
               opts={[["", "Tümü"], ...UNIT_TYPES.map((s) => [s, s])]} />
          <Sel l="Oda Tipi" v={form.room_type} on={(v) => setForm({ ...form, room_type: v })}
               opts={[["", "Tümü"], ...ROOM_TYPES.map((s) => [s, s])]} t="bulk-room-type" />
          <Sel l="Cephe" v={form.aspect} on={(v) => setForm({ ...form, aspect: v })}
               opts={[["", "Tümü"], ...ASPECTS.map((s) => [s, s])]} />
          <Sel l="Manzara" v={form.view} on={(v) => setForm({ ...form, view: v })}
               opts={[["", "Tümü"], ...VIEWS.map((s) => [s, s])]} />
        </div>

        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Hedef Durumlar (varsayılan: Satışta, Güncellemede)</div>
          <div className="flex flex-wrap gap-2">
            {UNIT_STATUSES.map((s) => (
              <button key={s} type="button" onClick={() => toggleStatus(s)}
                      className={`px-3 py-1.5 text-xs rounded-full border ${form.statuses.includes(s) ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 text-slate-700"}`}>
                {s}
              </button>
            ))}
          </div>
          {(form.statuses.includes("Rezerve") || form.statuses.includes("Kapora Alındı") || form.statuses.includes("Satıldı")) && (
            <label className="mt-2 inline-flex items-center gap-2 text-xs text-amber-700 bg-amber-50 px-3 py-1.5 rounded">
              <input type="checkbox" checked={form.force_locked} onChange={(e) => setForm({ ...form, force_locked: e.target.checked })} />
              Kilitli durumlara da uygula (DİKKAT)
            </label>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Sel l="Güncelleme Tipi" v={form.update_type} on={(v) => setForm({ ...form, update_type: v })}
               opts={[
                 ["percent_inc", "Yüzdesel Artış (%)"],
                 ["amount_add", "Sabit Tutar Ekle"],
                 ["amount_sub", "Sabit Tutar Düş"],
                 ["set", "Direkt Yeni Fiyat"],
               ]} t="update-type" />
          <label className="block">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1">Değer</div>
            <input type="number" className="input" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} data-testid="bulk-value" />
          </label>
        </div>

        <div className="flex items-center gap-3">
          <button onClick={preview} className="px-4 py-2 text-sm bg-white border border-slate-200 rounded-lg" data-testid="bulk-preview-btn">Önizle</button>
          <button onClick={apply} className="px-4 py-2 text-sm bg-amber-500 hover:bg-amber-600 text-white rounded-lg" data-testid="bulk-apply-btn">Uygula</button>
          {previewRows !== null && <span className="text-sm text-slate-700">Eşleşen: <b data-testid="bulk-match-count">{previewRows.length}</b> bölüm</span>}
        </div>

        {previewRows !== null && previewRows.length > 0 && (
          <div className="border border-slate-200 rounded-lg overflow-auto max-h-80" data-testid="bulk-preview-table">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 sticky top-0 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-3 py-2">Bölüm</th>
                  <th className="text-left px-3 py-2">Oda</th>
                  <th className="text-left px-3 py-2">Durum</th>
                  <th className="text-right px-3 py-2">Eski Fiyat</th>
                  <th className="text-right px-3 py-2">Yeni Fiyat</th>
                </tr>
              </thead>
              <tbody>
                {previewRows.slice(0, 100).map((u) => (
                  <tr key={u.id} className="border-t border-slate-100">
                    <td className="px-3 py-2 font-medium">{u.unit_no}</td>
                    <td className="px-3 py-2 text-slate-600">{u.room_type}</td>
                    <td className="px-3 py-2 text-slate-600">{u.status}</td>
                    <td className="px-3 py-2 text-right">{formatPrice(u.price, u.currency)}</td>
                    <td className="px-3 py-2 text-right font-semibold text-amber-700">{formatPrice(u.new_price, u.currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {previewRows.length > 100 && <div className="px-3 py-2 text-xs text-slate-500 bg-slate-50">… ve {previewRows.length - 100} bölüm daha</div>}
          </div>
        )}

        {result && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-sm" data-testid="bulk-result">
            Toplu fiyat güncelleme tamamlandı. <b>{result.updated}</b> bölüm güncellendi.
          </div>
        )}
      </div>

      {showLockedConfirm && (
        <div className="fixed inset-0 z-50 !mt-0 flex items-center justify-center bg-black/40 p-4" onClick={() => setShowLockedConfirm(false)} data-testid="bulk-locked-confirm">
          <div className="bg-white rounded-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="text-lg font-semibold text-slate-900 mb-3 font-display">Dikkat</div>
            <p className="text-sm text-slate-700">
              Bu işlem <b>Rezerve, Kapora Alınmış</b> veya <b>Satılmış</b> bağımsız bölümlerin fiyatını değiştirebilir.
              Devam etmek istediğinizden emin misiniz?
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setShowLockedConfirm(false)} className="px-4 py-2 text-sm border border-slate-200 rounded-lg">Vazgeç</button>
              <button onClick={confirmLocked} className="px-4 py-2 text-sm bg-red-600 hover:bg-red-700 text-white rounded-lg" data-testid="confirm-locked-apply">Yine de Uygula</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Sel({ l, v, on, opts, t }) {
  return (
    <label className="block">
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1">{l}</div>
      <select value={v} onChange={(e) => on(e.target.value)} className="input" data-testid={t}>
        {opts.map(([val, lab]) => <option key={val} value={val}>{lab}</option>)}
      </select>
    </label>
  );
}
